import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, RotateCcw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import ConfirmDialog from "@/components/common/ConfirmDialog";
import { errMsg, formatDate, useApi } from "@/lib/api";

const PROVIDER = (m) => (m.startsWith("claude") ? "Anthropic" : m.startsWith("gpt") ? "OpenAI" : m.startsWith("gemini") ? "Google" : "fal.ai");
const num = (v) => Number(String(v).replace(",", "."));

function PriceInput({ value, official, onChange, testid }) {
  const changed = value !== "" && num(value) !== official;
  return (
    <Input type="number" min="0" step="0.01" value={value} onChange={(e) => onChange(e.target.value)} title={`Ufficiale: ${official}`} data-testid={testid}
      className={`h-8 w-24 text-right tabular-nums text-xs ${changed ? "border-amber-400 bg-amber-50" : ""}`} />
  );
}

export default function PriceList() {
  const api = useApi();
  const [d, setD] = useState(null);
  const [llm, setLlm] = useState({});
  const [img, setImg] = useState({});
  const [busy, setBusy] = useState("");
  const [confirm, setConfirm] = useState(false);
  const fill = (x) => {
    setD(x);
    setLlm(Object.fromEntries(x.llm.map((r) => [r.model, { in: String(r.in), out: String(r.out) }])));
    setImg(Object.fromEntries(x.images.map((r) => [r.model, String(r.usd)])));
  };
  useEffect(() => { api.get("/pricing").then(({ data }) => fill(data)).catch(() => setD(false)); }, [api]);
  const run = async (key, fn, ok) => {
    setBusy(key);
    try { const { data } = await fn(); fill(data); toast.success(ok); } catch (e) { toast.error(errMsg(e)); }
    setBusy("");
  };
  const save = () => run("save", () => api.put("/pricing", {
    llm: Object.fromEntries(Object.entries(llm).map(([m, v]) => [m, { in: num(v.in), out: num(v.out) }])),
    images: Object.fromEntries(Object.entries(img).map(([m, v]) => [m, num(v)])),
  }), "Listino salvato: vale per le prossime stime, lo storico resta invariato");
  if (!d) return <p className="text-sm text-slate-500">{d === false ? "Listino non disponibile" : "Caricamento…"}</p>;
  return (
    <div className="space-y-4" data-testid="price-list">
      <p className="text-xs text-slate-500" data-testid="price-list-meta">
        {d.unit}. {d.custom ? `Listino personalizzato (modificato ${formatDate(d.updated_at, true)})` : `Prezzi ufficiali al ${formatDate(d.official_date)}`}.
        {" "}Fonti: {Object.entries(d.sources).map(([k, u], i) => <span key={k}>{i ? " · " : ""}<a href={u} target="_blank" rel="noreferrer" className="text-sky-700 hover:underline">{k}</a></span>)}
      </p>
      <div className="overflow-x-auto rounded-lg border">
        <table className="w-full text-sm" data-testid="price-list-llm">
          <thead className="bg-slate-50 text-[11px] uppercase tracking-wider text-slate-500">
            <tr><th className="px-3 py-2 text-left font-medium">Modello</th><th className="px-3 py-2 text-left font-medium">Provider</th><th className="px-3 py-2 text-right font-medium">Input $/1M</th><th className="px-3 py-2 text-right font-medium">Output $/1M</th><th className="px-3 py-2 text-right font-medium">Ufficiale</th></tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {d.llm.map((r) => (
              <tr key={r.model} data-testid={`price-row-${r.model}`}>
                <td className="px-3 py-1.5 font-mono text-xs text-slate-800 whitespace-nowrap">{r.model}</td>
                <td className="px-3 py-1.5 text-xs text-slate-500">{PROVIDER(r.model)}</td>
                <td className="px-3 py-1.5"><div className="flex justify-end"><PriceInput value={llm[r.model]?.in ?? ""} official={r.official.in} onChange={(v) => setLlm({ ...llm, [r.model]: { ...llm[r.model], in: v } })} testid={`price-in-${r.model}`} /></div></td>
                <td className="px-3 py-1.5"><div className="flex justify-end"><PriceInput value={llm[r.model]?.out ?? ""} official={r.official.out} onChange={(v) => setLlm({ ...llm, [r.model]: { ...llm[r.model], out: v } })} testid={`price-out-${r.model}`} /></div></td>
                <td className="px-3 py-1.5 text-right text-[11px] tabular-nums text-slate-400 whitespace-nowrap">{r.official.in} / {r.official.out}</td>
              </tr>
            ))}
            {d.images.map((r) => (
              <tr key={r.model} data-testid={`price-row-${r.model}`}>
                <td className="px-3 py-1.5 font-mono text-xs text-slate-800 whitespace-nowrap">{r.model}</td>
                <td className="px-3 py-1.5 text-xs text-slate-500">Immagini · per immagine</td>
                <td className="px-3 py-1.5" colSpan={2}><div className="flex justify-end"><PriceInput value={img[r.model] ?? ""} official={r.official} onChange={(v) => setImg({ ...img, [r.model]: v })} testid={`price-img-${r.model}`} /></div></td>
                <td className="px-3 py-1.5 text-right text-[11px] tabular-nums text-slate-400">{r.official}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="text-[11px] text-slate-500">Le modifiche valgono solo per le stime e i consuntivi futuri: ogni chiamata già registrata conserva il prezzo valido in quel momento.</p>
      <div className="flex flex-wrap justify-end gap-2">
        <Button variant="ghost" size="sm" onClick={() => setConfirm(true)} disabled={!!busy || !d.custom} data-testid="price-list-reset-btn"><RotateCcw size={13} className="mr-1.5" />Ripristina prezzi ufficiali</Button>
        <Button size="sm" onClick={save} disabled={!!busy} className="bg-sky-600 hover:bg-sky-700" data-testid="price-list-save-btn">{busy === "save" && <Loader2 size={13} className="mr-1.5 animate-spin" />}Salva listino</Button>
      </div>
      <ConfirmDialog open={confirm} onOpenChange={setConfirm} title="Ripristinare i prezzi ufficiali?" confirmLabel="Ripristina"
        description="Le tue modifiche al listino vengono eliminate. Lo storico dei costi non cambia." onConfirm={() => { setConfirm(false); run("reset", () => api.post("/pricing/reset"), "Prezzi ufficiali ripristinati"); }} />
    </div>
  );
}
